import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { fetchFaqs, Faq as FaqType } from '@/api/faq';
import { useState } from 'react';
import { formatDateTime } from '@/utils/dateUtils';

const Faq = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const filter = params.get('filter') || '전체';
  const keyword = params.get('keyword')?.trim().toLowerCase() || '';
  const [visibleCount, setVisibleCount] = useState(10);

  const { data, isLoading, isError } = useQuery<FaqType[]>({
    queryKey: ['Faqs'],
    queryFn: fetchFaqs,
  });

  const sorted = [...(data ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const filtered = sorted.filter((item) => {
    const title = item.question.toLowerCase();
    const content = item.answer.toLowerCase();
    if (!keyword) return true;
    if (filter === '내용') return content.includes(keyword);
    return title.includes(keyword) || content.includes(keyword);
  });

  const sliced = filtered.slice(0, visibleCount);

  return (
    <ListWrapper>
      {isLoading ? (
        <NoResultText>불러오는 중...</NoResultText>
      ) : isError ? (
        <NoResultText>데이터 로딩 실패</NoResultText>
      ) : sliced.length === 0 ? (
        <NoResultText>뉴스가 없습니다</NoResultText>
      ) : (
        sliced.map((item) => {
          const date = formatDateTime(item.createdAt);
          return (
            <ListItem key={item.id} onClick={() => navigate(`/intro/faq/${item.id}`)}>
              <DateText>{date}</DateText>
              <ContentRow>
                <DayBox>{item.id}</DayBox>
                <ContentText>{item.question}</ContentText>
              </ContentRow>
            </ListItem>
          );
        })
      )}

      <Wrapper>
        <StyledButton onClick={() => setVisibleCount((prev) => prev + 10)}>
          <ButtonText>더보기</ButtonText>
        </StyledButton>
      </Wrapper>
    </ListWrapper>
  );
};

export default Faq;

// ✅ styled-components

const ListWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border-top: 1px solid #000;
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px 12px 20px 12px;
  border-bottom: 1px solid var(--table-line-table_line01, #bdbdbd);

  &:hover {
    background-color: #f0f3fe;
    color: #0b46dd;
  }
`;

const DateText = styled.div`
  color: var(--gray-ui_gray04, #6f6f6f);
  text-align: right;
  font-size: 12px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.24px;
  text-align: start;
`;

const ContentRow = styled.div`
  display: flex;
  padding: 16px 0px;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const DayBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--gray-ui_gray05, #999);
  font-size: 16px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.32px;
`;

const ContentText = styled.div`
  flex: 1;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 16px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.32px;
  text-align: start;
`;

const NoResultText = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 0;
  color: var(--gray-ui_gray04, #6f6f6f);
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.32px;
`;

//BUTTON

const Wrapper = styled.div`
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  background: #fff;
`;

const StyledButton = styled.button`
  display: flex;
  height: 48px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  border-radius: 4px;
  background: var(--Primary-white, #fff);
  border: none;
  cursor: pointer;
`;

const ButtonText = styled.span`
  flex: 1 0 0;
  color: var(--gray-ui_gray04, #6f6f6f);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 16.8px */
  letter-spacing: -0.28px;
`;
